import React, { forwardRef, useImperativeHandle, useRef, useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../context-provider';
import { PopoverInner } from './popover-inner';
import Transition from '../transition';
import { useEvent, usePosition } from './hooks';
import { cls } from '@arco-design/mobile-utils';
import Portal from '../portal';
/** FIXME: 页面是否已经有一个气泡漏出，页面只能同时出现一个气泡 */

var hasShowPopover = false;
export var Popover = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      _props$innerPopoverCl = props.innerPopoverClassName,
      innerPopoverClassName = _props$innerPopoverCl === void 0 ? '' : _props$innerPopoverCl,
      _props$style = props.style,
      style = _props$style === void 0 ? {} : _props$style,
      _props$theme = props.theme,
      theme = _props$theme === void 0 ? 'black' : _props$theme,
      _props$needShadow = props.needShadow,
      needShadow = _props$needShadow === void 0 ? false : _props$needShadow,
      _props$content = props.content,
      content = _props$content === void 0 ? '' : _props$content,
      _props$transitionTime = props.transitionTimeout,
      transitionTimeout = _props$transitionTime === void 0 ? 300 : _props$transitionTime,
      _props$minWidth = props.minWidth,
      minWidth = _props$minWidth === void 0 ? '10px' : _props$minWidth,
      _props$maxWidth = props.maxWidth,
      maxWidth = _props$maxWidth === void 0 ? '90vw' : _props$maxWidth,
      _props$transitionName = props.transitionName,
      transitionName = _props$transitionName === void 0 ? 'fade' : _props$transitionName,
      _props$clickSelfToClo = props.clickSelfToClose,
      clickSelfToClose = _props$clickSelfToClo === void 0 ? true : _props$clickSelfToClo,
      _props$touchSelfToClo = props.touchSelfToClose,
      touchSelfToClose = _props$touchSelfToClo === void 0 ? props.touchToClose === void 0 ? false : props.touchToClose : _props$touchSelfToClo,
      _props$defaultVisible = props.defaultVisible,
      defaultVisible = _props$defaultVisible === void 0 ? false : _props$defaultVisible,
      visible = props.visible,
      children = props.children,
      _props$bordered = props.bordered,
      bordered = _props$bordered === void 0 ? props.theme === 'white' : _props$bordered,
      _props$showCloseIcon = props.showCloseIcon,
      showCloseIcon = _props$showCloseIcon === void 0 ? false : _props$showCloseIcon,
      _props$textSuffix = props.textSuffix,
      textSuffix = _props$textSuffix === void 0 ? null : _props$textSuffix,
      _props$mode = props.mode,
      mode = _props$mode === void 0 ? 'follow' : _props$mode,
      _props$showMask = props.showMask,
      showMask = _props$showMask === void 0 ? true : _props$showMask,
      _props$maskTransition = props.maskTransitionTimeout,
      maskTransitionTimeout = _props$maskTransition === void 0 ? {
    enter: 450,
    exit: 240
  } : _props$maskTransition,
      onChange = props.onChange,
      onClickCloseIcon = props.onClickCloseIcon,
      onClickTextSuffix = props.onClickTextSuffix,
      onClickMask = props.onClickMask;

  var _useContext = useContext(GlobalContext),
      prefixCls = _useContext.prefixCls;

  var _useState = useState(false),
      visibleState = _useState[0],
      setVisibleState = _useState[1];

  var visibleRef = useRef(visibleState);
  var wrapperRef = useRef(null);
  var childRef = useRef(null);
  var popoverRef = useRef(null);

  var _usePosition = usePosition(props, popoverRef, childRef, wrapperRef),
      direction = _usePosition.direction,
      position = _usePosition.position,
      isCalcPosition = _usePosition.isCalcPosition,
      transformOrigin = _usePosition.transformOrigin,
      computedChildAndPopoverOffset = _usePosition.computedChildAndPopoverOffset,
      arrowWidth = _usePosition.arrowWidth,
      resetPosition = _usePosition.resetPosition,
      adjustVerticalDirection = _usePosition.adjustVerticalDirection;
  /**
   * 气泡状态切换
   */


  var onVisibleChange = function onVisibleChange(newVisible) {
    if (newVisible === void 0) {
      newVisible = true;
    }

    // 为了防止退出中有缩放动画影响气泡位置计算，在退出动画结束前不可切换到展示气泡状态
    if (newVisible && !isCalcPosition) {
      return;
    }

    onChange == null ? void 0 : onChange(newVisible);

    if (visible === void 0) {
      setVisibleState(newVisible);
    }
  };

  var _useEvent = useEvent(props, wrapperRef, popoverRef, visibleRef, onVisibleChange, adjustVerticalDirection),
      popoverDisappear = _useEvent.popoverDisappear,
      mayAddEventOnBody = _useEvent.mayAddEventOnBody,
      startCloseTimer = _useEvent.startCloseTimer,
      mayRemoveEventListenerOnBody = _useEvent.mayRemoveEventListenerOnBody;

  useImperativeHandle(ref, function () {
    var _popoverRef$current;

    return {
      dom: wrapperRef.current,
      innerPopoverDom: ((_popoverRef$current = popoverRef.current) == null ? void 0 : _popoverRef$current.dom) || null,
      updatePosition: updatePosition
    };
  });
  useEffect(function () {
    if (defaultVisible || visible) {
      setVisibleState(true);
    }

    return function () {
      mayRemoveEventListenerOnBody();
    };
  }, []);
  useEffect(function () {
    if (visible !== void 0) {
      setVisibleState(Boolean(visible));
    }
  }, [visible]);
  useEffect(function () {
    // 非 visibleState 变化引起的回调，仅用于更新位置
    if (visibleRef.current) {
      computedChildAndPopoverOffset();
    }
  }, [children, content, direction]);
  useEffect(function () {
    visibleState && showPopover();
    visibleRef.current = visibleState;
    hasShowPopover = visibleState;

    if (!visibleState) {
      mayRemoveEventListenerOnBody();
      setTimeout(function () {
        !visibleRef.current && resetPosition();
      }, (typeof transitionTimeout === 'object' ? transitionTimeout.exit : transitionTimeout) || 300);
    }
  }, [visibleState]);

  function showPopover() {
    startCloseTimer();
    setTimeout(mayAddEventOnBody, 0); // 为了防止缩放动画影响气泡位置计算，气泡漏出阶段不重新计算位置

    if (!isCalcPosition) {
      return;
    } // 异步计算气泡位置，等待气泡渲染


    setTimeout(function () {
      computedChildAndPopoverOffset();
    }, 0);
  }

  function updatePosition() {
    if (!visibleState) return;
    computedChildAndPopoverOffset();
  }

  function onClickWrapper() {
    var visibleRefValue = visibleRef.current;

    if (!visibleRefValue && !hasShowPopover) {
      onVisibleChange(true);
      return;
    }

    if (visibleRefValue && clickSelfToClose && !touchSelfToClose) {
      popoverDisappear();
    }
  }

  function onTouchWrapper() {
    // 仅处理气泡消失，气泡出现交给onClick来处理
    if (touchSelfToClose && visibleRef.current) {
      popoverDisappear();
    }
  }

  var renderInnerPopover = function renderInnerPopover() {
    return /*#__PURE__*/React.createElement(PopoverInner, {
      className: cls(theme + "-theme", innerPopoverClassName, {
        bordered: bordered
      }),
      ref: popoverRef,
      direction: direction,
      minWidth: minWidth,
      maxWidth: maxWidth,
      position: position,
      arrowWidth: arrowWidth,
      transformOrigin: transformOrigin,
      needShadow: needShadow,
      content: content,
      mode: mode,
      showCloseIcon: showCloseIcon,
      textSuffix: textSuffix,
      onClickCloseIcon: onClickCloseIcon,
      onClickTextSuffix: onClickTextSuffix
    });
  };

  var renderPopover = function renderPopover() {
    return /*#__PURE__*/React.createElement(Portal, {
      getContainer: function getContainer() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return mode === 'global' ? document.body : wrapperRef.current;
      }
    }, visibleState && isCalcPosition && renderInnerPopover(), /*#__PURE__*/React.createElement(Transition, {
      in: visibleState && !isCalcPosition,
      timeout: transitionTimeout,
      type: transitionName,
      mountOnEnter: true,
      unmountOnExit: true
    }, renderInnerPopover()));
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, mode === 'global' && showMask && /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(Transition, {
    in: visibleState && !isCalcPosition,
    timeout: maskTransitionTimeout,
    type: "fade",
    mountOnEnter: true,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    className: prefixCls + "-popover-mask",
    onClick: onClickMask
  }))), /*#__PURE__*/React.createElement("div", {
    className: cls(prefixCls + "-popover", className, theme + "-theme", mode + "-mode", {
      bordered: bordered,
      'custom-content': typeof content !== 'string'
    }),
    style: style,
    ref: wrapperRef,
    onClick: onClickWrapper,
    onTouchStart: onTouchWrapper
  }, renderPopover(), /*#__PURE__*/React.createElement("div", {
    ref: childRef,
    className: "popover-child-inner"
  }, children)));
});